import type { FC, InputHTMLAttributes, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";
import clsx from "clsx";
import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon } from "@hugeicons/core-free-icons";

interface InputProps extends InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputStyles> {
	label?: string;
	required?: boolean;
	prefixes?: ReactNode;
	suffixes?: ReactNode;
	labelRight?: ReactNode;
	error?: string;
}

const inputStyles = tv({
	base: [
		"w-full text-sm font-normal border border-brand-dark/10 rounded-lg bg-brand-light-raised",
		"py-3 pr-4 pl-4",
		"focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 outline-none",
		"transition-colors duration-150",
		"hover:border-brand-dark/70"
	],
	variants: {
		hasIcon: {
			true: "pl-10",
			false: ""
		}
	}
});

export const ErrorMessage: FC<{ children?: ReactNode }> = ({ children }) => {
	return (
		<div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-2 text-sm font-normal text-red-600">
			<div>
				<HugeiconsIcon size={20} icon={AlertCircleIcon} />
			</div>
			<span>{children}</span>
		</div>
	);
};

export const Input: FC<InputProps> = ({
	label,
	required,
	prefixes,
	suffixes,
	className,
	labelRight,
	error,
	...rest
}) => {
	const hasIcon = !!prefixes;

	return (
		<div className="flex flex-col gap-1">
			{label && (
				<div className="flex items-center justify-between">
					<label className="text-brand-dark/70 text-xs font-medium">
						{label}
						{required && <span className="ml-1 text-red-600">*</span>}
					</label>
					{labelRight && <span className="text-brand-dark/70 text-sm">{labelRight}</span>}
				</div>
			)}

			{error && <ErrorMessage>{error}</ErrorMessage>}

			<div className="bg-brand-light-raised relative w-full rounded-lg">
				{hasIcon && (
					<div className="text-brand-dark/70 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 transform">
						{prefixes}
					</div>
				)}
				<input
					{...rest}
					className={clsx(inputStyles({ hasIcon }), error && "border-red-500 bg-red-50", className)}
				/>
				{suffixes && (
					<div className="text-brand-dark/70 absolute top-1/2 right-3 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center *:-m-3 *:flex *:p-3">
						{suffixes}
					</div>
				)}
			</div>
		</div>
	);
};
